/**
 * Hook de logique Grand Déplacement et zones pour la feuille d'heures.
 * Extrait de useTimesheetActions pour réduire sa taille et isoler la logique GD/zone.
 */
import { useCallback } from 'react';
import { dateKey } from '../utils/dateUtils';
import { getZoneRank } from '../utils/zoneUtils';
import type { Project, DiversSubProject } from '../types';
import type { GridRow, DailyIndemnity } from '../pages/timesheet/types';

interface UseTimesheetGDParams {
  rows: GridRow[];
  indemnities: Record<string, DailyIndemnity>;
  weekDays: Date[];
  holidays: string[];
  trainingDays: string[];
  projects: Project[];
  diversSubProjects: DiversSubProject[];
  /**
   * BE strict (bureau_etude). Les apprentis BE (apprenti_be) font aussi
   * du chantier, donc leurs zones Transport/Trajet doivent être calculées
   * comme un employé chantier classique. On utilise donc `isBE35h` qui
   * cible les vrais BE (cf. Timesheet.tsx).
   */
  isBE35h: boolean;
  isOutsideEntryMonth: (day: Date) => boolean;
}

export function useTimesheetGD(params: UseTimesheetGDParams) {
  const {
    rows, indemnities, weekDays, holidays, trainingDays,
    projects, diversSubProjects, isBE35h, isOutsideEntryMonth,
  } = params;

  // --- RÈGLE GD : DERNIER JOUR DE LA SEMAINE = PANIER REPAS ---
  const getGDWorkDaysInfo = useCallback((
    currentRows: GridRow[]
  ): { gdWorkDays: string[], lastGDDay: string | null } => {
    const gdWorkDays: string[] = [];

    weekDays.forEach(day => {
      const dKey = dateKey(day);
      const dayOfWeek = day.getDay();

      if (dayOfWeek === 0 || dayOfWeek === 6) return;
      if (holidays.includes(dKey)) return;
      if (trainingDays.includes(dKey)) return;
      if (isOutsideEntryMonth(day)) return;

      const hasGDHours = currentRows.some(row => {
        const hours = row.hours[dKey] || 0;
        if (hours > 0) {
          const project = projects.find(p => p.id === row.projectId);
          return project && project.zone === 'GD';
        }
        return false;
      });

      if (hasGDHours) {
        gdWorkDays.push(dKey);
      }
    });

    gdWorkDays.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const lastGDDay = gdWorkDays.length > 0 ? gdWorkDays[gdWorkDays.length - 1] : null;

    return { gdWorkDays, lastGDDay };
  }, [weekDays, holidays, trainingDays, projects, isOutsideEntryMonth]);

  // Helper pour trouver le projet GD dominant pour un jour donné
  const getGDProjectForDay = useCallback((dateStr: string): Project | null => {
    let bestProject: Project | null = null;
    let maxHours = 0;

    rows.forEach(row => {
      const hours = row.hours[dateStr] || 0;
      if (hours > 0) {
        const project = projects.find(p => p.id === row.projectId);
        if (project && project.zone === 'GD' && hours > maxHours) {
          maxHours = hours;
          bestProject = project;
        }
      }
    });

    if (!bestProject) {
      let weeklyMaxHours = 0;
      rows.forEach(row => {
        const project = projects.find(p => p.id === row.projectId);
        if (project && project.zone === 'GD') {
          const totalProjectHours = Object.values(row.hours).reduce((sum, h) => sum + (h || 0), 0);
          if (totalProjectHours > weeklyMaxHours) {
            weeklyMaxHours = totalProjectHours;
            bestProject = project;
          }
        }
      });
    }

    if (!bestProject) {
      for (const row of rows) {
        const project = projects.find(p => p.id === row.projectId);
        if (project && project.zone === 'GD') {
          bestProject = project;
          break;
        }
      }
    }

    return bestProject;
  }, [rows, projects]);

  // --- LOGIQUE ZONE GAGNANTE ---
  const calculateDailyWinningZone = useCallback((dateStr: string): string | number | null => {
    if (trainingDays.includes(dateStr)) return null;
    if (indemnities[dateStr]?.is_grand_deplacement === true) return null;
    // Vrais BE (bureau_etude) : pas de zone (ils ne font pas de chantier).
    // Les apprentis BE en revanche font du chantier → zone calculée comme
    // un employé chantier classique (cf. ticket Salaheddine avril 2026).
    if (isBE35h) return null;

    const contenders: { hours: number, zone: string | number }[] = [];

    rows.forEach(row => {
      const hours = row.hours[dateStr] || 0;
      if (hours > 0) {
        const savedZone = row.zones[dateStr];

        let defaultZone: string | number | null | undefined = null;
        if (row.projectId.startsWith('divers:')) {
          const subId = row.projectId.replace('divers:', '');
          const subProject = diversSubProjects.find(s => s.id === subId);
          if (subProject) {
            const parentProject = projects.find(p => p.id === subProject.parent_project_id);
            defaultZone = subProject.zone ?? parentProject?.zone;
          }
        } else {
          const project = projects.find(p => p.id === row.projectId);
          defaultZone = project?.zone;
        }

        const zone = savedZone !== undefined && savedZone !== null ? savedZone : defaultZone;

        if (zone !== undefined && zone !== null && zone !== 0 && zone !== '0') {
          contenders.push({ hours, zone });
        }
      }
    });

    if (contenders.length === 0) return null;

    contenders.sort((a, b) => {
      if (b.hours !== a.hours) return b.hours - a.hours;
      return getZoneRank(b.zone) - getZoneRank(a.zone);
    });

    return contenders[0].zone;
  }, [rows, indemnities, trainingDays, isBE35h, projects, diversSubProjects]);

  return { getGDWorkDaysInfo, getGDProjectForDay, calculateDailyWinningZone };
}
